import AjaxClass from './Validation/ajaxClass';

const ERROR_CLASS = 'is-invalid';
const ERROR_MESSAGE_CLASS = 'form__error';
const GROUP_SELECTOR = '.form__group, .form__privacy, .checkbox-group, .template-form__form-holder, .template-form__form-check';

function getGroup(field) {
    return field.closest(GROUP_SELECTOR) || field.parentElement;
}

function getOrCreateErrorElement(field) {
    const group = getGroup(field);
    if (!group) return null;
    let err = group.querySelector(`:scope > .${ERROR_MESSAGE_CLASS}`);
    if (!err) {
        err = document.createElement('div');
        err.className = ERROR_MESSAGE_CLASS;
        err.setAttribute('aria-live', 'polite');
        group.appendChild(err);
    }
    return err;
}

function setFieldError(field, message) {
    field.classList.add(ERROR_CLASS);
    const group = getGroup(field);
    if (group) group.classList.add('show--error');
    if (field.type === 'hidden') {
        const trigger = field.closest('.form__dropdown')?.querySelector('.form__dropdown-trigger');
        if (trigger) trigger.classList.add(ERROR_CLASS);
    }
    const err = getOrCreateErrorElement(field);
    if (err) err.textContent = message;
}

function clearFieldError(field) {
    field.classList.remove(ERROR_CLASS);
    const group = getGroup(field);
    if (group) {
        group.classList.remove('show--error');
        const err = group.querySelector(`:scope > .${ERROR_MESSAGE_CLASS}`);
        if (err) err.textContent = '';
    }
    if (field.type === 'hidden') {
        const trigger = field.closest('.form__dropdown')?.querySelector('.form__dropdown-trigger');
        if (trigger) trigger.classList.remove(ERROR_CLASS);
    }
}

function digitsOnly(str) {
    return String(str || '').replace(/\D/g, '');
}

function validateField(field) {
    if (field.disabled || field.hasAttribute('notRequired')) return null;
    if (!field.required) return null;

    const value = (field.value || '').trim();
    const name = field.name || '';

    if (field.type === 'checkbox') {
        if (!field.checked) {
            if (name === 'privacy-policy') return 'Você precisa concordar com a Política de Privacidade.';
            return 'Marque esta opção para continuar.';
        }
        return null;
    }

    if (!value) {
        if (name === 'real_state_product') return 'Selecione um produto de interesse.';
        if (name === 'name') return 'Informe seu nome completo.';
        if (name === 'email') return 'Informe seu e-mail.';
        if (name === 'phone') return 'Informe seu telefone.';
        if (name === 'cv') return 'Anexe seu currículo.';
        if (name === 'area') return 'Selecione uma área de interesse.';
        return 'Este campo é obrigatório.';
    }

    if (field.type === 'email' || name === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!emailRegex.test(value)) return 'Digite um e-mail válido (ex: nome@exemplo.com).';
    }

    if (field.type === 'tel' || field.hasAttribute('phone') || name === 'phone') {
        const digits = digitsOnly(value);
        if (digits.length < 10 || digits.length > 11) return 'Digite um telefone válido com DDD.';
    }

    if (name === 'name') {
        if (value.length < 3) return 'Informe seu nome completo.';
        if (/\d/.test(value)) return 'O nome não pode conter números.';
    }

    return null;
}

function validateForm(form) {
    const fields = Array.from(form.querySelectorAll('input, select, textarea'));
    let firstInvalid = null;

    fields.forEach(field => {
        if (field.name === '_token') return;
        if (field.type === 'hidden' && !field.required) return;

        const error = validateField(field);
        if (error) {
            setFieldError(field, error);
            if (!firstInvalid) firstInvalid = field;
        } else {
            clearFieldError(field);
        }
    });

    return firstInvalid;
}

function focusInvalidField(field) {
    let target = field;
    if (field.type === 'hidden') {
        target = field.closest('.form__dropdown')?.querySelector('.form__dropdown-trigger') || field;
    }
    // Pages running their own Lenis (snap-scroll) hijack native scroll, so
    // scrollIntoView gets reverted. Use Lenis directly when present.
    const lenis = window.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
        const offset = -Math.max(120, window.innerHeight / 3);
        try {
            lenis.scrollTo(target, { offset, duration: 0.8, force: true, lock: false });
        } catch (e) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    } else {
        try {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
            target.scrollIntoView();
        }
    }
    setTimeout(() => {
        if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    }, 350);
}

const VALIDATION_MODAL_TITLE = 'Verifique os dados do formulário';
const AJAX_ERROR_MODAL_TITLE = 'Erro ao enviar formulário';

function setErrorModalTexts(title, descriptionHtml) {
    const modal = document.getElementById('error-message-forms');
    if (!modal) return;
    const titleEl = modal.querySelector('.modal-title');
    const descEl = modal.querySelector('.description');
    if (titleEl && title != null) titleEl.textContent = title;
    if (descEl && descriptionHtml != null) descEl.innerHTML = descriptionHtml;
}

function openErrorModal() {
    const modal = document.getElementById('error-message-forms');
    if (modal) modal.classList.add('open-modal');
}

function showValidationModal(form) {
    const messages = [];
    const seen = new Set();
    form.querySelectorAll('.show--error').forEach(group => {
        const errEl = group.querySelector(':scope > .form__error');
        const text = errEl ? errEl.textContent.trim() : '';
        if (text && !seen.has(text)) { seen.add(text); messages.push(text); }
    });

    let descriptionHtml;
    if (messages.length === 0) {
        descriptionHtml = 'Por favor, preencha todos os campos obrigatórios corretamente.';
    } else if (messages.length === 1) {
        descriptionHtml = escapeHtml(messages[0]);
    } else {
        descriptionHtml = '<ul class="form__error-summary">' +
            messages.map(m => `<li>${escapeHtml(m)}</li>`).join('') +
            '</ul>';
    }

    setErrorModalTexts(VALIDATION_MODAL_TITLE, descriptionHtml);
    openErrorModal();
}

function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

function attachLiveClear(form) {
    form.querySelectorAll('input, select, textarea').forEach(field => {
        if (field.name === '_token') return;

        if (field.type === 'checkbox') {
            field.addEventListener('change', () => clearFieldError(field));
        } else if (field.type === 'hidden') {
            const dropdown = field.closest('.form__dropdown');
            if (dropdown) {
                dropdown.querySelectorAll('.form__dropdown-option').forEach(opt => {
                    opt.addEventListener('click', () => {
                        setTimeout(() => {
                            if (field.value) clearFieldError(field);
                        }, 0);
                    });
                });
            }
        } else {
            field.addEventListener('input', () => clearFieldError(field));
            field.addEventListener('change', () => clearFieldError(field));
            field.addEventListener('blur', () => {
                const error = validateField(field);
                if (error) setFieldError(field, error);
                else clearFieldError(field);
            });
        }
    });
}

function resetFormState(form) {
    form.classList.remove('was-validated');
    form.querySelectorAll(`.${ERROR_CLASS}`).forEach(f => f.classList.remove(ERROR_CLASS));
    form.querySelectorAll(`.${ERROR_MESSAGE_CLASS}`).forEach(f => (f.textContent = ''));
    form.querySelectorAll('.show--error').forEach(g => g.classList.remove('show--error'));
    form.querySelectorAll('.form__dropdown').forEach(d => {
        const lbl = d.querySelector('.js-dropdown-label, #dropdown-selected');
        if (lbl) lbl.textContent = 'Selecione';
        const trig = d.querySelector('.form__dropdown-trigger');
        if (trig) {
            trig.classList.remove('has-value');
            trig.classList.remove(ERROR_CLASS);
        }
        d.querySelectorAll('.form__dropdown-option.is-selected').forEach(o => o.classList.remove('is-selected'));
    });
}

export default function bootstrapForms() {
    document.querySelectorAll('form.needs-validation').forEach(formElement => {
        if (formElement.classList.contains('custom')) {
            return false;
        }

        attachLiveClear(formElement);

        formElement.addEventListener('submit', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const form = this;
            const firstInvalid = validateForm(form);

            if (firstInvalid) {
                form.classList.add('was-validated');
                focusInvalidField(firstInvalid);
                showValidationModal(form);
                return false;
            }

            if (window.dataLayer && Array.isArray(window.dataLayer)) {
                const formName =
                    form.getAttribute('data-name')?.trim() ||
                    form.id ||
                    'Formulário';

                window.dataLayer.push({
                    event: 'formSubmit',
                    formName: formName
                });
            }

            let fields = Array.from(new FormData(form).entries()).map(([name, value]) => ({ name, value }));
            const images = form.querySelectorAll('input[type=file]');
            if (images.length > 0) {
                images.forEach(el => {
                    const image = { name: el.name, value: el.files[0] };
                    fields.push(image);
                });
            }

            const formData = new FormData(form);
            if (images.length > 0) {
                fields.forEach(obj => {
                    if (!obj.value) obj.value = '';
                    formData.append(obj.name, obj.value);
                });
            }

            form.disabled = true;
            const type = 'POST';
            const url = form.action;
            const dataType = 'json';

            const modalSuccess = document.getElementById('success-message-forms');
            const modalSuccessDescription = document.querySelector('#success-message-forms .description');
            const Loader = document.getElementById('loading-forms');
            if (Loader) Loader.classList.add('open-modal');

            class Form extends AjaxClass {
                successFunction(data, form) {
                    if (Loader) Loader.classList.remove('open-modal');
                    if (data.status === true) {
                        form.reset();
                        resetFormState(form);
                        form.disabled = false;
                        if (data.redirect) {
                            window.location = data.redirect;
                        } else if (modalSuccess && modalSuccessDescription) {
                            modalSuccess.classList.add('open-modal');
                            modalSuccessDescription.innerHTML = data.txt;
                        }
                    } else {
                        setErrorModalTexts(AJAX_ERROR_MODAL_TITLE, data.txt);
                        openErrorModal();
                        form.disabled = false;
                    }
                }

                errorFunction(xhr, textStatus, errorThrown) {
                    if (Loader) Loader.classList.remove('open-modal');
                    let description = 'Ocorreu um erro. Tente novamente mais tarde.';

                    if (errorThrown === 'Too Many Requests' || (xhr && xhr.responseText === 'Too Many Requests')) {
                        description = 'Muitas tentativas! Aguarde um minuto antes de tentar novamente.';
                    }

                    if (xhr && xhr.status === 419) {
                        description = 'Sessão expirada. Recarregando...';
                        setTimeout(() => location.reload(), 2000);
                    }

                    setErrorModalTexts(AJAX_ERROR_MODAL_TITLE, description);
                    openErrorModal();
                    form.disabled = false;
                }
            }

            const ajax = new Form(form, type, url, dataType, formData);
            ajax.send();

            return false;
        });
    });

    document.querySelectorAll('[phone]').forEach(phoneElement => {
        phoneElement.addEventListener('keypress', function (event) {
            const charCode = event.which || event.keyCode;

            if ([8, 46, 9, 27, 13, 35, 36, 37, 39].includes(charCode) || (charCode === 190 && !this.value.includes('.'))) {
                return;
            }

            if (charCode < 48 || charCode > 57) {
                event.preventDefault();
            }
        });

        phoneElement.addEventListener('blur', function () {
            mascaraDeTelefone(this);
        });

        phoneElement.addEventListener('focus', function () {
            tiraHifen(this);
        });
    });

    function mascaraDeTelefone(telefone) {
        const textoAtual = telefone.value.replace(/[^0-9]/g, '');
        const isCelular = textoAtual.length >= 11;
        let textoAjustado;

        if (isCelular) {
            const parte1 = textoAtual.slice(0, 2);
            const parte2 = textoAtual.slice(2, 7);
            const parte3 = textoAtual.slice(7, 11);

            textoAjustado = `(${parte1}) ${parte2}${parte3 ? '.' + parte3 : ''}`;
        } else {
            const parte1 = textoAtual.slice(0, 2);
            const parte2 = textoAtual.slice(2, 6);
            const parte3 = textoAtual.slice(6, 10);

            textoAjustado = `(${parte1}) ${parte2}${parte3 ? '.' + parte3 : ''}`;
        }

        telefone.value = textoAjustado || null;
    }

    function tiraHifen(telefone) {
        telefone.value = telefone.value.replace(/\./g, '');
    }

    document.querySelectorAll('[cnpj]').forEach(cnpjElement => {
        cnpjElement.addEventListener('keypress', function (event) {
            const charCode = event.which || event.keyCode;

            if ([8, 46, 9, 27, 13, 35, 36, 37, 39].includes(charCode)) {
                return;
            }

            if (charCode < 48 || charCode > 57) {
                event.preventDefault();
            }
        });

        cnpjElement.addEventListener('blur', function () {
            aplicarMascaraCNPJ(this);
        });

        cnpjElement.addEventListener('focus', function () {
            removerMascaraCNPJ(this);
        });
    });

    function aplicarMascaraCNPJ(cnpj) {
        const textoAtual = cnpj.value.replace(/[^0-9]/g, '');

        const parte1 = textoAtual.slice(0, 2);
        const parte2 = textoAtual.slice(2, 5);
        const parte3 = textoAtual.slice(5, 8);
        const parte4 = textoAtual.slice(8, 12);
        const parte5 = textoAtual.slice(12, 14);

        cnpj.value = `${parte1}.${parte2}.${parte3}/${parte4}-${parte5}`;
    }

    function removerMascaraCNPJ(cnpj) {
        cnpj.value = cnpj.value.replace(/[^0-9]/g, '');
    }

    document.querySelectorAll('[cpf]').forEach(cpfElement => {
        cpfElement.addEventListener('keypress', function (event) {
            const charCode = event.which || event.keyCode;

            // Permitir teclas de controle
            if ([8, 46, 9, 27, 13, 35, 36, 37, 39].includes(charCode)) {
                return;
            }

            // Bloquear qualquer caractere que não seja número
            if (charCode < 48 || charCode > 57) {
                event.preventDefault();
            }
        });

        cpfElement.addEventListener('blur', function () {
            aplicarMascaraCPF(this);
        });

        cpfElement.addEventListener('focus', function () {
            removerMascaraCPF(this);
        });
    });

    function aplicarMascaraCPF(cpf) {
        const textoAtual = cpf.value.replace(/[^0-9]/g, '');

        const parte1 = textoAtual.slice(0, 3);
        const parte2 = textoAtual.slice(3, 6);
        const parte3 = textoAtual.slice(6, 9);
        const parte4 = textoAtual.slice(9, 11);

        // Formato: 000.000.000-00
        cpf.value = `${parte1}${parte2 ? '.' + parte2 : ''}${parte3 ? '.' + parte3 : ''}${parte4 ? '-' + parte4 : ''}`;
    }

    function removerMascaraCPF(cpf) {
        cpf.value = cpf.value.replace(/[^0-9]/g, '');
    }


    document.querySelectorAll('[cep]').forEach(cepElement => {
        cepElement.addEventListener('keypress', function (event) {
            const charCode = event.which || event.keyCode;

            // Permite teclas como backspace, delete, tab, enter e setas
            if ([8, 46, 9, 27, 13, 35, 36, 37, 39].includes(charCode)) {
                return;
            }

            // Restringe entrada apenas a números
            if (charCode < 48 || charCode > 57) {
                event.preventDefault();
            }
        });

        cepElement.addEventListener('blur', function () {
            aplicarMascaraCEP(this);
        });

        cepElement.addEventListener('focus', function () {
            removerMascaraCEP(this);
        });
    });

    function aplicarMascaraCEP(cep) {
        const textoAtual = cep.value.replace(/[^0-9]/g, '');

        const parte1 = textoAtual.slice(0, 5);
        const parte2 = textoAtual.slice(5, 8);

        cep.value = `${parte1}-${parte2}`;
    }

    function removerMascaraCEP(cep) {
        cep.value = cep.value.replace(/[^0-9]/g, '');
    }

    document.querySelectorAll('[documento]').forEach(docElement => {
        docElement.addEventListener('keypress', function (event) {
            const charCode = event.which || event.keyCode;

            // Permitir teclas de controle
            if ([8, 46, 9, 27, 13, 35, 36, 37, 39].includes(charCode)) {
                return;
            }

            // Bloquear qualquer caractere que não seja número
            if (charCode < 48 || charCode > 57) {
                event.preventDefault();
            }
        });

        docElement.addEventListener('blur', function () {
            aplicarMascaraDocumento(this);
        });

        docElement.addEventListener('focus', function () {
            removerMascaraDocumento(this);
        });
    });

    function aplicarMascaraDocumento(el) {
        const texto = el.value.replace(/\D/g, '');

        if (texto.length <= 11) {
            // CPF: 000.000.000-00
            const parte1 = texto.slice(0, 3);
            const parte2 = texto.slice(3, 6);
            const parte3 = texto.slice(6, 9);
            const parte4 = texto.slice(9, 11);
            el.value = `${parte1}${parte2 ? '.' + parte2 : ''}${parte3 ? '.' + parte3 : ''}${parte4 ? '-' + parte4 : ''}`;
        } else {
            // CNPJ: 00.000.000/0000-00
            const parte1 = texto.slice(0, 2);
            const parte2 = texto.slice(2, 5);
            const parte3 = texto.slice(5, 8);
            const parte4 = texto.slice(8, 12);
            const parte5 = texto.slice(12, 14);
            el.value = `${parte1}${parte2 ? '.' + parte2 : ''}${parte3 ? '.' + parte3 : ''}${parte4 ? '/' + parte4 : ''}${parte5 ? '-' + parte5 : ''}`;
        }
    }

    function removerMascaraDocumento(el) {
        el.value = el.value.replace(/\D/g, '');
    }

}
